import React, { useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Button, Card, Checkbox, Form, Spin, Tooltip } from 'antd'
import CardOrBankAccount from '../../../../Platform/Shared/Payment/CardOrBankAccount'
import PaymentMethodList from '../../../../Platform/Shared/PaymentMethodList'
import {
  selectCompanyDefaultPaymentMethod,
  selectIsUpdatingCompany,
  selectIsFetchingCurrentUser,
} from '@reducers/currentUser/currentUserSelectors'
import { CARD, BANK_ACCOUNT } from '@components/Signup/constants'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'

import './PaymentStep.scss'

const Payment = ({ goToPreviousStep, goToNextStep, stepName, nextStepName }) => {
  useSignupPageView('Account Setup Payment')
  const track = useSignupAnalyticsTracker()

  const [submitting, setSubmitting] = useState(false)
  const formRef = useRef(null)

  const defaultPaymentMethod = useSelector(selectCompanyDefaultPaymentMethod)
  const isUpdatingCompany = useSelector(selectIsUpdatingCompany)
  const isFetchingCurrentUser = useSelector(selectIsFetchingCurrentUser)
  const isCard = defaultPaymentMethod && defaultPaymentMethod.id ? defaultPaymentMethod.id.substr(0, 4) === 'card' : false

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const { skip_payment } = await formRef.current.validateFields()

      track({
        eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`,
        payload: {
          skip_payment,
          payment_type: skip_payment ? null : isCard ? CARD : BANK_ACCOUNT,
        },
      })
      goToNextStep()
    } catch (error) {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    track({ eventName: `Account Setup Back Button Clicked ${stepName}` })
    goToPreviousStep()
  }

  const handleFeesInvolvedHover = visible => {
    if (visible) { track({ eventName: 'Account Setup Fees Involved Clicked' }) }
  }

  const onAddCard = () => track({ eventName: 'Account Setup Add A Card Button Clicked' })
  const onClickLinkBankAccount = () => track({ eventName: 'Account Setup Link Bank Account Clicked' })
  const onClickSwitchToBankAccount = () => track({ eventName: 'Account Setup Switch To Bank Account Clicked' })

  return (
    <Form ref={formRef} validateTrigger="onSubmit" data-testid="signup-payment-step">
      <Spin
        tip="loading..."
        spinning={isUpdatingCompany || isFetchingCurrentUser}
        size="large"
      >
        <Card>
          <h3>Payment Information</h3>
          <p>
            <span style={{ color: '#FF0000' }}>*&nbsp;</span>Connect a card or
            bank account for fees associated with accepting orders.{' '}
            <Tooltip
              title="Most lenders have technology fees that are required to accept an order. If a lender has a fee, you will be prompted to agree to this fee before being charged. Adding a payment method now will save time and allow you to start accepting orders faster."
              onVisibleChange={handleFeesInvolvedHover}
              color='black'
              arrowPointAtCenter={true}
            >
              <span className="ant-btn ant-btn-link">
                What fees are involved?
              </span>
            </Tooltip>
          </p>
          {defaultPaymentMethod
            ? (
            <PaymentMethodList />
              )
            : (
            <React.Fragment>
              <CardOrBankAccount
                onAddCard={onAddCard}
                onClickLinkBankAccount={onClickLinkBankAccount}
                onClickSwitchToBankAccount={onClickSwitchToBankAccount}
              />
              <Form.Item
                name="skip_payment"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                          new Error(
                            'Please add a card, link an account, or check skip and provide later',
                          ),
                        ),
                  },
                ]}
              >
                <Checkbox name="skip_payment" data-testid="signup-payment-skip-checkbox">Skip and provide later</Checkbox>
              </Form.Item>
            </React.Fragment>
              )}
        </Card>
      </Spin>
      <Card className="controls">
        <Button type="default" onClick={handleBack} data-testid="signup-payment-back-button">
          Back
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={submitting} data-testid="signup-payment-continue-button">
          {submitting ? <Spin /> : nextStepName}
        </Button>
      </Card>
    </Form>
  )
}

export default Payment
